import { useMemo } from "react"

/**
 * Builds a case-insensitive duplicate-name checker from a list of existing records.
 * Pure (no hooks) so it can be unit tested directly. See FIQ-1635.
 *
 * @param {Array<{id: string, name: string}>} items - existing records of this type.
 * @param {string} [excludeId] - id of the record being renamed (ignored as self).
 * @returns {(name: string) => boolean} true when `name` collides with another record.
 */
export function buildDuplicateNameChecker(items, excludeId) {
    const taken = new Set(
        (items ?? [])
            .filter((item) => item?.id !== excludeId)
            .map((item) => normalizeName(item?.name))
            .filter(Boolean)
    )
    return function isDuplicateName(name) {
        const normalized = normalizeName(name)
        return !!normalized && taken.has(normalized)
    }
}

/**
 * Memoized hook wrapper around {@link buildDuplicateNameChecker}.
 *
 * @param {Array<{id: string, name: string}>} items - existing records of this type.
 * @param {{ excludeId?: string }} [options]
 * @returns {(name: string) => boolean}
 */
export function useDuplicateNameCheck(items, { excludeId } = {}) {
    return useMemo(() => buildDuplicateNameChecker(items, excludeId), [items, excludeId])
}

/**
 * Builds a `BoundTextField` `yup` validator that rejects duplicate names. Pairs with
 * DataInputDialog's yup-based OK gating, so the Create/Save button disables and the
 * message shows as inline helper text. Mirrors the server-side assertUniqueName guard.
 *
 * @param {Array<{id: string, name: string}>} items - existing records of this type.
 * @param {string} label - human-readable record type, e.g. "rate card".
 * @param {{ excludeId?: string }} [options]
 * @returns {(value: string, yup: object) => string} validator for the `yup` prop.
 */
export function useUniqueNameValidator(items, label, { excludeId } = {}) {
    const isDuplicateName = useDuplicateNameCheck(items, { excludeId })
    return function validateUniqueName(value, yup) {
        return yup
            .string()
            .trim()
            .required()
            .test(
                "duplicate-name",
                `A ${label} with the name "${(value ?? "").trim()}" already exists`,
                () => !isDuplicateName(value)
            )
            .validateSync(value)
    }
}

function normalizeName(name) {
    return (name ?? "").trim().toLowerCase()
}
